import { useCallback, useEffect, useState } from "react";
import { DatePicker, Segmented, Spin, Tooltip } from "antd";
import { getUnixTime, format } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import analyticsApi from "../../../api/analytics-api";
import paymentApi from "../../../api/paymentApi";
import { cacheGet, cacheSet } from "../../../utils/dashboard-cache";
import DashboardStatCards from "./dashboard-stat-cards";
import DashboardRevenueChart from "./dashboard-revenue-chart";
import DashboardPlatformChart from "./dashboard-platform-chart";
import DashboardQuickActions from "./dashboard-quick-actions";
import DashboardRecentActivity from "./dashboard-recent-activity";
import DashboardDailyStatsHistory from "./dashboard-daily-stats-history";
import DashboardAccountDailyStats from "./dashboard-account-daily-stats";
import { MetricInfo, buildMetricDocs, TIME_FIELD_LABELS } from "./dashboard-metric-info";

const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
// Kiểu thời gian đã chọn được nhớ lại giữa các lần vào trang.
const TIME_FIELD_STORAGE_KEY = "dashboard_time_field";

const EMPTY_SUMMARY = {
    totalCommission: 0,
    totalUserCommission: 0,
    totalOrders: 0,
    pendingOrders: null,
    pendingCommission: null,
    totalPaid: 0,
    pendingPayments: 0,
    platforms: [],
};

const TIME_FIELD_OPTIONS = [
    { label: TIME_FIELD_LABELS.purchase, value: "purchase" },
    { label: TIME_FIELD_LABELS.complete, value: "complete" },
];

function readStoredTimeField() {
    try {
        const v = localStorage.getItem(TIME_FIELD_STORAGE_KEY);
        return v === "complete" ? "complete" : "purchase";
    } catch (_) {
        return "purchase";
    }
}

function DashBoard() {
    const [summary, setSummary] = useState(EMPTY_SUMMARY);
    const [dailyData, setDailyData] = useState([]);
    const [recentPayments, setRecentPayments] = useState([]);
    const [loading, setLoading] = useState(true);       // full-screen spinner
    const [refreshing, setRefreshing] = useState(false); // subtle background indicator
    const [lastUpdated, setLastUpdated] = useState(null);
    const [filter, setFilter] = useState({ startDate: undefined, endDate: undefined });
    const [timeField, setTimeField] = useState(readStoredTimeField);

    const isFiltered = !!(filter.startDate || filter.endDate);
    const cacheKey = `summary_${timeField}`;
    const docs = buildMetricDocs(timeField);

    const fetchData = useCallback(
        async (background = false) => {
            if (background) setRefreshing(true);
            else setLoading(true);

            try {
                const [summaryRes, dailyRes, paymentsRes] = await Promise.all([
                    analyticsApi.getSummary({ ...filter, timeField }),
                    analyticsApi.getDaily(30, timeField),
                    paymentApi.getAll("limit=10&page=1"),
                ]);

                const newSummary = summaryRes.data.data || EMPTY_SUMMARY;
                const newDailyData = (dailyRes.data.data || []).map((r) => ({
                    date: r.date,
                    totalCommission: Number(r.totalCommission) || 0,
                    totalUserCommission: Number(r.totalUserCommission) || 0,
                }));
                const newPayments = paymentsRes.data.data?.payments || [];

                setSummary(newSummary);
                setDailyData(newDailyData);
                setRecentPayments(newPayments);
                setLastUpdated(new Date());

                // Persist to localStorage for the next visit (unfiltered only)
                if (!isFiltered) {
                    cacheSet(cacheKey, {
                        summary: newSummary,
                        dailyData: newDailyData,
                        recentPayments: newPayments,
                    });
                }
            } catch (err) {
                console.error("[DashBoard] fetch error:", err);
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [filter, isFiltered, timeField, cacheKey]
    );

    useEffect(() => {
        // Stale-while-revalidate: show cache instantly, then refresh in background
        const cached = !isFiltered ? cacheGet(cacheKey, CACHE_TTL_MS) : null;
        if (cached) {
            setSummary(cached.value.summary);
            setDailyData(cached.value.dailyData);
            setRecentPayments(cached.value.recentPayments);
            setLastUpdated(new Date(cached.cachedAt));
            setLoading(false);
            fetchData(true); // silent background refresh
        } else {
            fetchData(false); // full load with spinner
        }
    }, [fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

    const onSelectDate = (value) => {
        if (!value) {
            setFilter({ startDate: undefined, endDate: undefined });
            return;
        }
        setFilter({
            startDate: getUnixTime(value[0]["$d"]),
            endDate: getUnixTime(value[1]["$d"]),
        });
    };

    const onChangeTimeField = (value) => {
        setTimeField(value);
        try {
            localStorage.setItem(TIME_FIELD_STORAGE_KEY, value);
        } catch (_) {
            // Ignore write failures (quota exceeded, private mode)
        }
    };

    return (
        <div className="p-6 w-full overflow-auto bg-surface-sunken min-h-full">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold text-content m-0">Tổng quan</h1>
                    {/* Subtle refresh spinner shown during background re-fetch */}
                    {refreshing && (
                        <Spin size="small" />
                    )}
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                    {lastUpdated && (
                        <span className="text-xs text-content-3">
                            Cập nhật lúc {format(lastUpdated, "HH:mm:ss")}
                        </span>
                    )}
                    <Tooltip title="Làm mới dữ liệu">
                        <button
                            onClick={() => fetchData(false)}
                            disabled={loading || refreshing}
                            className="p-1.5 rounded-md text-content-3 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-40"
                        >
                            <FontAwesomeIcon
                                icon={faArrowsRotate}
                                className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
                            />
                        </button>
                    </Tooltip>
                    {/* Chọn mốc thời gian gom đơn: ngày đặt đơn (mặc định) vs ngày sàn duyệt */}
                    <div className="flex items-center gap-1">
                        <Segmented
                            size="small"
                            value={timeField}
                            options={TIME_FIELD_OPTIONS}
                            onChange={onChangeTimeField}
                        />
                        <MetricInfo doc={docs.timeField} placement="bottomRight" />
                    </div>
                    <DatePicker.RangePicker
                        onChange={onSelectDate}
                        placeholder={["Từ ngày", "Đến ngày"]}
                    />
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <Spin size="large" />
                </div>
            ) : (
                <div className="flex flex-col gap-6">
                    <DashboardStatCards summary={summary} timeField={timeField} />

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2">
                            <DashboardRevenueChart data={dailyData} timeField={timeField} />
                        </div>
                        <div>
                            <DashboardPlatformChart
                                platforms={summary.platforms}
                                timeField={timeField}
                            />
                        </div>
                    </div>

                    <DashboardQuickActions />

                    <DashboardDailyStatsHistory />

                    <DashboardAccountDailyStats />

                    <DashboardRecentActivity payments={recentPayments} />
                </div>
            )}
        </div>
    );
}

export default DashBoard;
